import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { FormSection } from '@app/core/components/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Grid, Stack } from '@mui/material';
import { FormContainer, SwitchElement, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { FacebookPublishValues, useFacebookPublishSchema } from './useFacebookPublishSchema';

type FacebookPublishFormProps = BaseExportFormProps<
  socialExport.FacebookPublishDto,
  socialExport.FacebookSettingsDto
>;

export function FacebookPublishForm({
  source,
  onUpload,
  defaultSettings,
  videoUrl,
  actions,
}: FacebookPublishFormProps) {
  const { $t } = useIntl();
  const schema = useFacebookPublishSchema();
  const initialValues: FacebookPublishValues = {
    title: source.title ?? '',
    description: source.description ?? '',
    thumbType: 'DEFAULT',
    thumbnailUrl: null,
    thumbOffsetMs: null,
    private: defaultSettings?.private ?? true,
    publishAt: null,
  };

  const beforeSubmit = (data: FacebookPublishValues): socialExport.FacebookPublishDto => ({
    title: data.title,
    description: data.description,
    thumbnailUrl:
      data.thumbType === 'IMAGE_URL' || data.thumbType === 'VIDEO_FRAME'
        ? (data.thumbnailUrl ?? undefined)
        : undefined,
    private: data.private,
    publishAt: data.publishAt || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <FormSection
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <Stack spacing={3}>
          <TextFieldElement
            name="title"
            label={$t({
              id: 'socialExport.form.title',
              defaultMessage: 'Título',
            })}
            required
            fullWidth
          />
          <TextFieldElement
            name="description"
            label={$t({
              id: 'socialExport.form.description',
              defaultMessage: 'Descripción',
            })}
            required
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.thumbnailAndSettings',
          defaultMessage: 'Thumbnail y ajustes',
        })}
      >
        <Grid container spacing={4}>
          <Grid item md={6}>
            <ThumbFieldsMui showFrameImageToField maxSizeMb={1} videoUrl={videoUrl} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <Stack spacing={2}>
              <SwitchElement
                name="private"
                label={$t({
                  id: 'socialExport.form.uploadAsPrivate',
                  defaultMessage: 'Cargar como video privado',
                })}
                color="primary"
              />
            </Stack>
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.schedule',
          defaultMessage: 'Programación',
        })}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSection>
      {actions}
    </FormContainer>
  );
}
